import { useRef, useState, useEffect } from "react";
import { Popover, Checkbox, Button } from "@datapay-by-datacom/design-system";
import { PillButton } from "../../../shared/components/DatePill/PillButton";
import styles from "./ControlBar.module.css";

const STATUS_OPTIONS = ["completed", "running", "failed", "terminated"];

export interface StatusPillProps {
  status: string[];
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onChange: (status: string[]) => void;
}

function formatStatusValue(status: string[]): string {
  if (status.length === 0) return "All";
  if (status.length === 1) return status[0];
  return `${status.length} selected`;
}

export function StatusPill({
  status,
  isOpen,
  onOpenChange,
  onChange,
}: StatusPillProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [pending, setPending] = useState<string[]>(status);

  useEffect(() => {
    if (isOpen) {
      setPending(status);
    }
  }, [isOpen, status]);

  const isActive = status.length > 0;

  function handleToggle(value: string, checked: boolean) {
    setPending((prev) =>
      checked ? [...prev, value] : prev.filter((s) => s !== value),
    );
  }

  function handleApply() {
    onChange(pending);
    onOpenChange(false);
  }

  return (
    <>
      <PillButton
        label="Status"
        value={formatStatusValue(status)}
        isActive={isActive}
        isOpen={isOpen}
        triggerRef={triggerRef}
        onClick={() => onOpenChange(!isOpen)}
        testId="status-pill"
      />
      <Popover
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        placement="bottom"
      >
        <div className={styles.statusContent} data-testid="status-options">
          <div className={styles.checkboxList}>
            {STATUS_OPTIONS.map((s) => (
              <Checkbox
                key={s}
                value={s}
                isSelected={pending.includes(s)}
                onChange={(checked: boolean) => handleToggle(s, checked)}
              >
                <span className={styles.statusLabel}>{s}</span>
              </Checkbox>
            ))}
          </div>
          <div className={styles.statusActions}>
            <Button
              variant="primary"
              onPress={handleApply}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
}
